/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { type SaveState, SaveStatus } from '@ecsper/ui-panels';
import { type ReactElement, useState } from 'react';

export interface SaveControlsProps {
  saveState: SaveState;
  lastSavedAt?: string;
  saveError?: string;
  onSaveNow: () => void;
  onSaveMajorVersion: (versionNote: string) => void;
  onRetrySave?: () => void;
}

/**
 * Governed save controls: status indicator, manual save, and major version creation.
 */
export function SaveControls({
  saveState,
  lastSavedAt,
  saveError,
  onSaveNow,
  onSaveMajorVersion,
  onRetrySave,
}: SaveControlsProps): ReactElement {
  const [showMajorNote, setShowMajorNote] = useState(false);
  const [majorNote, setMajorNote] = useState('');

  const handleCreateMajor = (): void => {
    if (!showMajorNote) {
      setShowMajorNote(true);
      return;
    }
    onSaveMajorVersion(majorNote.trim());
    setMajorNote('');
    setShowMajorNote(false);
  };

  return (
    <section className="ecs-panel ecs-save-controls">
      <SaveStatus
        state={saveState}
        {...(lastSavedAt !== undefined ? { lastSavedAt } : {})}
        {...(saveError !== undefined ? { errorMessage: saveError } : {})}
        {...(onRetrySave !== undefined ? { onRetry: onRetrySave } : {})}
      />
      <div className="ecs-save-controls__actions">
        <button
          type="button"
          className="ecs-btn ecs-btn--secondary"
          disabled={saveState === 'saving'}
          onClick={() => {
            onSaveNow();
          }}
        >
          Save
        </button>
        <button
          type="button"
          className="ecs-btn ecs-btn--secondary"
          disabled={saveState === 'saving'}
          onClick={() => {
            handleCreateMajor();
          }}
        >
          Create Major Version
        </button>
      </div>
      {showMajorNote ? (
        <label className="ecs-save-controls__note">
          <span className="ecs-save-controls__note-label">Version note (optional)</span>
          <input
            className="ecs-input ecs-save-controls__note-input"
            type="text"
            value={majorNote}
            onChange={(ev) => {
              setMajorNote(ev.target.value);
            }}
            placeholder="Milestone description"
          />
        </label>
      ) : null}
    </section>
  );
}
